import { useMemo } from 'react';

import { useChartContext } from '@/components/Chart/context/ChartContext';
import ExportButton from '@/components/Chart/ExportButton';
import type { ChartType } from '@/components/Chart/types/highcharts';
import { useLightMode } from '@/context/LightModeContext';
import { Permission } from '@/entities/permissions';
import type { SymbolKey } from '@/entities/symbol';
import { overrideSymbols } from '@/features/SymbolSelector/store/symbolSelector.slice';
import { useAppDispatch } from '@/hooks/redux-hooks';
import { ExportSelectionSource } from '@/hooks/useOpenExportSelection';
import { useOptionalMarketWidget } from '@/pages/Markets/MarketsComponents/contexts/MarketWidgetContext';
import usePermission from '@/shared/hooks/usePermission';
import { openExportDrawer } from '@/slices/drawerSlice';
import { setExport } from '@/slices/exportSlice';

const ExportChartButton = ({
  disabled = false,
  graphType,
  metadata,
  tabName
}: {
  graphType: ChartType | 'fullscreen';
  disabled?: boolean;
  metadata?: Record<string, string>;
  tabName?: string;
}) => {
  const { hasPermission } = usePermission();
  const isDisabled = disabled || !hasPermission(Permission.ExportImage);

  const { chartSeries, chartRef } = useChartContext();
  const { isLightMode } = useLightMode();
  const { isFullScreen } = useOptionalMarketWidget();
  const dispatch = useAppDispatch();
  const type = useMemo(() => {
    const temp = graphType || 'spot';
    return temp === 'spot' && isFullScreen ? 'fullscreen' : temp;
  }, [graphType, isFullScreen]);

  const exportSelection = () => {
    if (chartSeries) {
      let selectedTimeRange: string | undefined = undefined;
      const selectedSymbols: SymbolKey[] = chartSeries.map(series => {
        const custom = series.custom;
        return {
          Code: custom.Code,
          Period: custom.Period || '',
          PeriodType: custom.PeriodType,
          TimeRef: custom.TimeRef,
          transactionDate: custom.transactionDate
        };
      });
      if (chartRef.current?.chart) {
        const chart = chartRef.current?.chart;
        const selectedTimeRangeButtonIndex = chart.rangeSelector?.selected;
        if (selectedTimeRangeButtonIndex !== undefined) {
          const buttonConfig = chart.rangeSelector.buttonOptions[selectedTimeRangeButtonIndex];
          if (buttonConfig) {
            selectedTimeRange = buttonConfig.text === 'Max' ? selectedTimeRange : buttonConfig.text;
          }
        }
      }
      dispatch(overrideSymbols({ symbols: selectedSymbols }));
      // setExport must be dispatched before openExportDrawer so that exportNameBase
      // is already in Redux state when the drawer mounts and reads it.
      dispatch(
        setExport({
          exportType: type,
          from: metadata?.from,
          isLightMode,
          selectedTimeRange,
          exportNameBase: tabName
        })
      );
      dispatch(
        openExportDrawer({ showHistory: false, source: ExportSelectionSource.CHART_WIDGET_BUTTON })
      );
    }
  };

  return (
    <ExportButton
      disabled={isDisabled}
      handleClick={exportSelection}
      title='Export chart'
    />
  );
};

export default ExportChartButton;
